import React, { FC, KeyboardEventHandler, useContext, useEffect, useState } from 'react';
import { Portal } from '../Portal';
import { ModalBackdrop } from './_Backdrop/ModalBackdrop';
import { getScrollbarWidth } from './utils';
import { MODAL_IMPORTANCE_LEVEL } from './consts';
import { IModalProps } from './models';
import { ModalContext, ModalProvider } from './context';

/** Компонент для отображения модального окна. */
export const ModalComponent: FC<IModalProps> = ({
  children,
  open: openProps,
  backdropTransitionClasses,
  closeOnBackdropClick = true,
  backdropClasses,
  backdropStyle,
  closeOnEscape = true,
  mode,
  onBackdropClick,
  onClose,
  importanceLevel = MODAL_IMPORTANCE_LEVEL.REGULAR,
}) => {
  const [open, setOpen] = useState(openProps);
  const [parent, setParent] = useState<HTMLElement | null>(null);
  const { parentTree } = useContext(ModalContext);

  const isCloseOnBackDrop = importanceLevel === MODAL_IMPORTANCE_LEVEL.REGULAR && closeOnBackdropClick;
  const isCloseOnEscape = importanceLevel === MODAL_IMPORTANCE_LEVEL.REGULAR && closeOnEscape;

  /** Для закрытия окна используем только этот метод, чтобы сохранить transition */
  const handleClose = () => {
    setOpen(false);
  };

  const onClosed = () => {
    onClose?.();
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isCloseOnBackDrop) return;

    if (onBackdropClick) {
      onBackdropClick(event);
    } else {
      closeOnBackdropClick && handleClose();
    }
  };

  const handleKeyUp: KeyboardEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();

    if (e.code === 'Escape' && isCloseOnEscape) {
      onClose?.();
    }
  };

  useEffect(() => {
    setOpen(openProps);

    if (parent) {
      if (openProps) {
        const scrollWidth = getScrollbarWidth();
        parent.style.overflow = 'hidden';
        parent.style.paddingRight = `${scrollWidth}px`;
      }

      return () => {
        /** Нужно, чтобы возвращали скролл только когда закрываем последнюю модалку */
        if (parentTree.length === 0) {
          parent.style.overflow = 'auto';
          parent.style.paddingRight = '0';
        }
      };
    }
  }, [openProps, parent, parentTree]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setParent(document.body);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!openProps) {
    return null;
  }

  return (
    <Portal>
      <ModalBackdrop
        style={backdropStyle}
        onKeyUp={handleKeyUp}
        onClick={handleBackdropClick}
        onBackdropClosed={onClosed}
        mode={mode}
        open={open}
        backdropTransitionClasses={backdropTransitionClasses}
        className={backdropClasses}
      >
        {children}
      </ModalBackdrop>
    </Portal>
  );
};

export const Modal: FC<IModalProps> = (props) => (
  <ModalProvider>
    <ModalComponent {...props} />
  </ModalProvider>
);
